import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useFormContext } from 'react-hook-form'

import { Fade } from 'react-awesome-reveal'

import Title from '@/components/common/Title'
import { Grid } from '@/styles/grid'
import { Parking } from '@/service'
import { useMarket } from '@/utils/multi-markets/context'
import { getParkingAddress } from '@/utils/getParkingAddress'
import { getParkingLabel } from '@/utils/getParkingLabel'
import { LoadMore } from '@/components/parkings/Results.styled'
import Button from '@/components/common/Button'
import Spinner from '@/components/common/SpinnerPage'
import { TitleWrapper } from '@/components/home/Discover/Discover.styled'
import { useFetchPaginationParkings } from '@/service/apiHooks/useFetchPaginationParkings'

import ParkingTile from '../ParkingTile'
import { Wrapper } from '../../SearchNearParking.styled'

import { SuggestionsListWrapper } from './NoResultsSuggestions.styled'

interface Props {
  latLon: [number, number] | undefined
  onMapModalOptn: () => void
}

const NoResultsSuggestions = ({ latLon, onMapModalOptn }: Props) => {
  const { t } = useTranslation('common')
  const { locale, market } = useMarket()
  const router = useRouter()
  const { getValues } = useFormContext()

  const size = 10

  const { parkings, isLoading } = useFetchPaginationParkings({
    params: {
      lang: locale,
      size: size,
      countryCode: market,
      sorting: 'distance;asc',
      page: 0,
      ...(latLon?.length && { latLon: `${latLon[0]},${latLon[1]}` })
    }
  })
  const itemsRepeat = 4
  const [counter, setCounter] = useState(itemsRepeat)

  const handleParkingClick = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
    parking: Parking
  ) => {
    e.stopPropagation()

    const startDate = getValues('startDate')
    const endDate = getValues('endDate')

    router.push({
      pathname: parking?.reservationAvailable
        ? '/reservation'
        : '/subscription',
      query: {
        ...(parking?.reservationAvailable && { startDate: startDate }),
        ...(parking?.reservationAvailable && { endDate: endDate }),
        parkingUuid: parking?.uuid,
        parking: parking?.urlSlug,
        bookType: parking?.reservationAvailable
          ? 'reservation'
          : 'subscription',
        ...(!parking?.reservationAvailable && { startDate: startDate }),
        latitude: parking.lat,
        longitude: parking.lon
      }
    })
  }

  return (
    <Fade triggerOnce>
      <Wrapper id="discover-section">
        <Grid>
          <TitleWrapper>
            <Title text={t('discover')} />
          </TitleWrapper>
        </Grid>
        {!isLoading && parkings?.length ? (
          <>
            <SuggestionsListWrapper>
              {parkings?.slice(0, counter).map((el, i: number) => {
                const description = getParkingAddress(el, t)
                const name = getParkingLabel(el, t)
                return (
                  <ParkingTile
                    key={el?.uuid}
                    name={name}
                    description={description}
                    imageUrl={el?.mainPictureUrl}
                    parking={el}
                    onClick={e => handleParkingClick(e, el)}
                    dataTestId={`parking-tile-${el.urlSlug}`}
                    onMapModalOptn={onMapModalOptn}
                  />
                )
              })}
            </SuggestionsListWrapper>
            {parkings && parkings.length > counter && (
              <LoadMore>
                <Button
                  onClick={() => setCounter(counter + itemsRepeat)}
                  text={t('results.button')}
                />
              </LoadMore>
            )}
          </>
        ) : (
          <Spinner />
        )}
      </Wrapper>
    </Fade>
  )
}

export default NoResultsSuggestions
